import Image from '@base/Image';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import { topologyLevels } from '@constants/index';
import { PRACTICE_STARTED } from '@constants/segment';
import { PRACTICE_FLOW_TYPE, PRACTICE_TYPE } from '@cont/Practice/constants';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import { getTopologyName } from '@models/Topology';
import { Fonts, Spacings } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import { pluralize } from '@utils/textHelper';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeStore from '@stores/planner/practiceStore';
import { finishPracticeSession } from '@api/fetchers/practice';

const checkIcon = 'schedule/check-violet.svg';
const rightIcon = 'schedule/chevron-right.svg';

const ActionButtonContainer = styled.div`
  padding: ${Spacings.SPACING_4B};
  padding-right: ${Spacings.SPACING_5B};
  border-radius: 0px 0px ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
  border-top: 0.5px solid
    ${({ $isDarkModeOn }) =>
      $isDarkModeOn ? 'var(--color-divider)' : 'var(--color-i-gray-v2)'};
  background-color: var(--color-base-1);
  display: grid;
  grid-template: 1fr / 8px 1fr max-content max-content;
  grid-column-gap: ${Spacings.SPACING_2};
  align-content: center;
  justify-content: center;
  cursor: pointer;
  grid-column: ${({ $gridColumn }) => $gridColumn};
  &:hover {
    background-color: var(--color-divider);
  }
`;

const LeftIndicator = styled.div`
  width: 2px;
  background-color: ${({ color }) => color};
  border-radius: 2px;
  height: 100%;
`;

const practiceIconSize = 16;

const SubTitle = styled(P2)`
  display: flex;
  align-items: center;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
`;

const PracticeCard = ({ item }) => {
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const segmentAnalytics = usePracticeSegmentAnalytics();
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const { setFinishSummary, toggleShowSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();

  const { topicGroup, attachments } = item;
  const quizzes = attachments?.quizzes;
  const quiz = quizzes[0];
  const isCompleted = quiz.status === 1;
  const onClick = async () => {
    if (isCompleted) {
      const { session_report } = await finishPracticeSession(quiz.session_uid);
      finishPlannerPracticeSession(session_report, true);
      setFinishSummary({
        goalSlug,
        goalUID,
        sessionId: quiz.session_uid,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        topicGroup: topicGroup.uid,
        topicUID: topicGroup.uid,
        quizUID: quiz.uid,
        educatorName: item.author.name
      });
      toggleShowSummary();
      return pushRoute(
        {
          pathname: '/goal/[goalSlug]/[goalUID]/practice/'
        },
        `/goal/${goalSlug}/${goalUID}/practice/`
      );
    }

    return pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
      query: {
        goalSlug,
        goalUID,
        topicUID: topicGroup.uid,
        topic_type: PRACTICE_TYPE.EACH_TOPIC,
        topic_group: topicGroup.uid,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        quiz_uid: quiz.uid,
        educator_name: item.author.name,
        source: 'Planner'
      }
    }).then(() => {
      segmentAnalytics(PRACTICE_STARTED, {
        topic_uid: null,
        practice_questions: quiz.num_questions,
        is_resumed: false,
        topology_id: topicGroup.uid,
        topology_name: topicGroup.title,
        topology_level: getTopologyName[topologyLevels.GROUP],
        last_primary_source_section: 'Planner',
        practice_type: 'Educator Led Practice',
        is_completed: isCompleted ? 'Yes' : 'No'
      });
    });
  };

  const completedQuestionCount = `${pluralize(
    quiz.num_questions || 0,
    'question'
  )}`;

  const borderLeftColor = isCompleted
    ? 'var(--color-i-violet-4)'
    : 'var(--color-text-secondary)';

  return (
    <ActionButtonContainer onClick={onClick} $isDarkModeOn={isDarkModeOn}>
      <LeftIndicator color={borderLeftColor} />
      <P1>Practice</P1>
      {!isCompleted && (
        <>
          <SubTitle>{completedQuestionCount}</SubTitle>
          <Image
            alt="Practice available"
            width={practiceIconSize}
            height={practiceIconSize}
            src={rightIcon}
          />
        </>
      )}
      {isCompleted && (
        <Image
          alt="Practice completed"
          width={practiceIconSize}
          height={practiceIconSize}
          src={checkIcon}
        />
      )}
    </ActionButtonContainer>
  );
};

const topicGroupPropType = PropTypes.shape({
  uid: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired
});

const quizzesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    uid: PropTypes.string.isRequired,
    status: PropTypes.number.isRequired,
    num_questions: PropTypes.number.isRequired
  })
);

PracticeCard.propTypes = {
  item: PropTypes.shape({
    topicGroup: topicGroupPropType.isRequired,
    attachments: PropTypes.shape({
      quizzes: quizzesPropType.isRequired
    })
  }).isRequired
};

export default PracticeCard;
